/**
 * Input contracts for the cascade-delete callables (ADR-018). These replace
 * the previous single-doc `deleteDoc` calls on `sessions/{id}` and
 * `events/{id}`, which left orphaned children behind (sessionInternals,
 * sessionNotes, feedback, and for events: nested sessions + their
 * children, plus eventMemberships, audiences, sponsors, notifications,
 * receipts, invitations, email deliveries).
 *
 * Direct client deletes on `sessions` and `events` are blocked at the
 * security rule layer — these callables are the only path.
 */

import { z } from 'zod';

export const deleteSessionInputSchema = z
  .object({
    eventId: z.string().min(1),
    sessionId: z.string().min(1),
  })
  .strict();

export type DeleteSessionInput = z.infer<typeof deleteSessionInputSchema>;

export interface DeleteSessionResponse {
  success: true;
  /** Number of descendant docs removed, excluding the session doc itself. */
  descendantsDeleted: number;
}

export const deleteEventInputSchema = z
  .object({
    eventId: z.string().min(1),
  })
  .strict();

export type DeleteEventInput = z.infer<typeof deleteEventInputSchema>;

export interface DeleteEventResponse {
  success: true;
  /**
   * Number of docs removed, excluding the event doc itself. Includes all
   * sessions and their descendants plus every event-scoped collection.
   */
  descendantsDeleted: number;
}

export const deleteUserInputSchema = z
  .object({
    /**
     * Auth UID of the target user. Self-delete when userId === caller uid;
     * otherwise the caller must have the global admin role. A personId is
     * also accepted (the admin person page holds that, not the uid): a
     * claimed person resolves to their uid through `persons.authUid`; an
     * unclaimed person is rejected with failed-precondition — use
     * deleteContact for those.
     */
    userId: z.string().min(1),
  })
  .strict();

export type DeleteUserInput = z.infer<typeof deleteUserInputSchema>;

export interface DeleteUserResponse {
  success: true;
  /**
   * Number of Firestore docs removed, excluding `users/{userId}` itself.
   * Does not include Storage blobs or the Firebase Auth record (those are
   * always one each when present).
   */
  descendantsDeleted: number;
}

export const deleteContactInputSchema = z
  .object({
    /**
     * Contact doc id. Contacts are pre-auth staging identities (no Auth
     * record yet), so there is no "self-delete" path — only admins can
     * delete contacts.
     */
    contactId: z.string().min(1),
  })
  .strict();

export type DeleteContactInput = z.infer<typeof deleteContactInputSchema>;

export interface DeleteContactResponse {
  success: true;
  /**
   * Number of `sent` invitations matching the contact's email that were
   * marked `revoked`. Invitations are preserved as audit trail per
   * ADR-089 — they're not deleted, just revoked so the email can't be
   * silently claimed by a future signup with the same address.
   */
  invitationsRevoked: number;
}
